import * as React from "react";
import Select, {
    Props as SelectProps,
    GroupBase,
    components as RSComponents,
    StylesConfig,
} from "react-select";

import CloseIcon from "../Icons/CloseIcon";
import { CreatableProps } from "react-select/creatable";
import Creatable from "react-select/creatable";

export interface LabelValueOption {
    label: string;
    value: string;
}

export const ClearIndicator = (p: any) => (
    <RSComponents.ClearIndicator {...p}>
        <CloseIcon width={24} height={24} />
    </RSComponents.ClearIndicator>
);

export const MultiValueRemove = (p: any) => (
    <RSComponents.MultiValueRemove {...p}>
        <CloseIcon width={20} height={20} />
    </RSComponents.MultiValueRemove>
);

function mergeStyles<Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
    userStyles?: StylesConfig<Option, IsMulti, Group>
): StylesConfig<Option, IsMulti, Group> {
    const defaultStyles: StylesConfig<Option, IsMulti, Group> = {
        menuPortal: (base) => ({
            ...base,
            zIndex: 1000021,
        }),
        menu: (base) => ({
            ...base,
            zIndex: 1000021,
        }),
    };

    if (!userStyles) return defaultStyles;

    // Merge shallowly by keys; for functions, wrap to compose
    const merged: StylesConfig<Option, IsMulti, Group> = { ...defaultStyles, ...userStyles };

    // If user provided menuPortal/menu, wrap to ensure z-index unless overridden
    if (userStyles.menuPortal) {
        const userFn = userStyles.menuPortal;
        merged.menuPortal = (base, state) => ({
            zIndex: 1000021,
            ...defaultStyles.menuPortal?.(base as any, state as any),
            ...(typeof userFn === "function" ? (userFn as any)(base, state) : userFn),
        }) as any;
    }
    if (userStyles.menu) {
        const userFn = userStyles.menu;
        merged.menu = (base, state) => ({
            zIndex: 1000021,
            ...defaultStyles.menu?.(base as any, state as any),
            ...(typeof userFn === "function" ? (userFn as any)(base, state) : userFn),
        }) as any;
    }

    return merged;
}

export function CustomSelectWrapper<
    Option,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>
>(props: SelectProps<Option, IsMulti, Group>) {
    const mergedStyles = React.useMemo(() => mergeStyles<Option, IsMulti, Group>(props.styles), [props.styles]);

    return (
        <Select
            {...props}
            className="react-select-container"
            classNamePrefix="react-select"
            menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
            menuPosition={props.menuPosition || "fixed"}
            styles={mergedStyles}
            components={{
                ...props.components,
                ClearIndicator,
                MultiValueRemove,
            }}
            placeholder={props.placeholder || "Select..."}
        />
    );
}

export function CreatableWrapper<
    Option,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>
>(props: CreatableProps<Option, IsMulti, Group>) {
    const mergedStyles = React.useMemo(() => mergeStyles<Option, IsMulti, Group>(props.styles), [props.styles]);

    return (
        <Creatable
            {...props}
            className="react-select-container"
            classNamePrefix="react-select"
            menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
            menuPosition={props.menuPosition || "fixed"}
            styles={mergedStyles}
            components={{
                ...props.components,
                ClearIndicator,
                MultiValueRemove,
            }}
            placeholder={props.placeholder || "Select..."}
        />
    );
}
